/**
 * `/studio/clients/$slug?section=files` — the studio's own files surface.
 *
 * ## Why it is here and not somewhere else
 *
 * `files_create_upload`, `files_release!`, `files_hold!`, `files_archive!` and
 * `files_request_review` have all existed since session 16, are all tested, and all
 * work. Until this session **not one of them had a screen** (R2-17): the studio's only
 * files surface was a seven-row summary behind a `<details>` in the People column,
 * which is `mockups/studio/client-detail.html` drawn faithfully and is a paragraph
 * rather than a console. So staff could not release a held handover when the final
 * invoice was paid, could not ask a client for a review, and could not read the answer
 * a client gave. `.docs/24` §The governing rule: "A capability the product promises
 * must be reachable from the interface."
 *
 * Three places it could have gone, and why this one:
 *
 *   **The People column, extended.** Refused. That column is one third of a
 *   `RegionCols` whose whole design is "three kinds of fact about one account, folded"
 *   (`client-detail.html`: "one region, three columns, no borders between them").
 *   A per-file list with a control on every row is not a fact, it is a workbench, and
 *   CONTRACT.md §2.12 puts a `.frow` list in a `.card__body--tight` — which a region
 *   column is not.
 *
 *   **`/studio/work-orders/$ref`,** the delivery console `.docs/24` §D3 plans.
 *   Refused as the *only* home: a work order can reach the four handover items and
 *   nothing else. Two hundred of Casaturca's two hundred and four files are monthly
 *   deliverables with no `work_order_id` at all, so release, hold, archive and
 *   request-review would still have no screen for 98% of the files they exist for —
 *   which is the same hole in a new place. D3 can link here; the two do not compete.
 *
 *   **A tab on the client, beside the other five.** Taken. Files belong to a
 *   **client**: that is what `file.client_id` says, what `pathFor` puts in the path,
 *   what `files_list`'s `client` filter takes, and what the client's own `/app/files`
 *   is. `?section=` is already the tab mechanism and the URL is already the state, so
 *   "look at their files" is a link somebody can send. The People tab keeps people and
 *   history and loses the files column to here, because one fact belongs in one place.
 *
 * ## What it shows
 *
 * The summary head is the mockup's, word for word, read from `clients_get_detail` —
 * so nothing was lost in the move. Under it, one `files_list` of the hundred most
 * recent files of every kind, newest first, with the ones that need a decision
 * promoted above the fold: held, out for review, or come back with changes. That
 * ordering is `MonthFiles.byAttentionFirst`'s, for the same reason.
 *
 * **Every control lives in one dialog per row.** Four buttons on each of a hundred
 * rows is four hundred buttons, and all four writes need a typed reason or note
 * anyway (`ReasonSchema` is `min 3`), so a row carries one *Manage* button and
 * `FileDialog` carries the rest. It also carries the review record, which is the
 * other half of R2-11: the client's answer was stored and shown on no screen in
 * either portal.
 *
 * There is deliberately **no filter bar.** CONTRACT.md §5 refused `.filters` — "the
 * bar was deleted from `clients.html` rather than shipped with inline `max-width` on
 * every control. If the affordance is wanted back, it needs a real decision, not a
 * width hack." The list is newest-first with attention first, which is the ordering a
 * filter would mostly be used to reproduce.
 */

import { useQueryClient } from '@tanstack/react-query'
import {
  ClientFilesSummarySchema,
  MAX_PAGE_LIMIT,
  type ClientFilesSummaryView,
  type FileView,
  type StudioClientDetailView,
} from '@fareast/shared'

import { moduleKey } from '../../../api/keys.js'
import { useStudioRead } from '../../../api/studio.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { FileRow } from '../../../components/portal/Files.js'
import { Row, ScrollX, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Pill } from '../../../components/portal/Status.js'
import { TBody, Table, Td, Tr } from '../../../components/portal/Table.js'
import { Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { extensionOf } from '../../client/files/fileType.js'
import { FileDialog } from './FileDialog.js'
import { UploadFileButton } from './UploadFileButton.js'

export const FILES_ID = 'files'

/**
 * How many rows sit on the card before the fold. Seven, which is what the client's
 * own files page shows and what the mockup's Contents fold lists.
 */
const ROWS_ON_THE_CARD = 7

export type FilesSectionProps = {
  readonly slug: string
  readonly detail: StudioClientDetailView
}

export function FilesSection({ slug, detail }: FilesSectionProps) {
  const format = useFormat()
  const queryClient = useQueryClient()
  /*
   * The summary is **parsed** rather than read, the same way `OpenItemsSection` reads
   * its own slice and for the same reason: `useStudioRead` types the answer from the
   * *handler's* return type and not from the schema, so nothing on this path applies
   * a schema default. `held_reasons` is new (R3-17) and is `undefined` in a recording
   * taken before it, which is neither `null` nor a shape `heldLine` can read. One
   * parse turns it into the `null` the branch below already has an answer for.
   */
  const files = ClientFilesSummarySchema.parse(detail.files)

  /*
   * The hundred most recent, of every kind.
   *
   * `inline: true` because this is a panel inside a page that has already answered:
   * a files list that failed should be one line in its own section, not the whole
   * client detail replaced by the route boundary's error.
   *
   * The input is recorded verbatim by `scripts/record-world.ts`. `studioApi` matches
   * a recording on its input, so a page that sent a different one would silently be
   * served the first `files_list` row in the file — which is another client's.
   */
  const list = useStudioRead(
    'files_list',
    { client: slug, limit: MAX_PAGE_LIMIT },
    { inline: true },
  )

  /**
   * What a write disturbs beyond its own module.
   *
   * `moduleOfAction('files_release')` is `files`, so the list above refetches on its
   * own. The **summary** is `clients_get_detail`, cached under `clients`, and
   * `api/keys.ts` `DOMAIN_ALSO_INVALIDATES` has no `files` row — so the held count in
   * the sentence would go on saying four after a release until something else moved.
   * `ActionForm` takes no `alsoInvalidates`, so the page says it once, here.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: moduleKey('clients') })
  }

  const rows = list.data?.files ?? []
  const ordered = byAttentionFirst(rows)
  const shown = ordered.slice(0, ROWS_ON_THE_CARD)
  const folded = ordered.slice(ROWS_ON_THE_CARD)

  return (
    <Section id={FILES_ID}>
      <SectionHead
        title="Files"
        note={`${format.qty(files.files, { one: 'file', other: 'files' })} · ${format.bytes(
          files.bytes,
        )}`}
        pushNote
      />

      <Card>
        <CardBody>
          <Stack gap="tight">
            {/*
              The mockup's own sentence, moved here with the panel it describes — and
              then split, because it was two claims from two different numbers (R3-17).

              "Only a project handover is gated on its final invoice" is about the
              payment gate; `files.held` is every file with no `released_at`. The two
              agreed until the studio held a deliverable, at which point the count went
              from four to five and the sentence went on naming one reason for all of
              them. `CLAUDE.md` §Files: "**Held is not gated** — a client's own brief is
              held for ever and is behind no payment", and a studio member reading this
              panel is exactly the person who needs to know which of the five will be
              freed by a payment and which will not.

              `held_reasons` is null in a payload recorded before the split, and then
              the panel says only the part it can still prove.
            */}
            <Prose>
              Deliverables are released as they are made, and only a project handover is gated on
              its final invoice. {heldLine(files, format)}
            </Prose>
            <Row>
              <UploadFileButton slug={slug} onDone={refresh} />
            </Row>
          </Stack>
        </CardBody>

        {list.isPending ? (
          <CardBody>
            <Empty>Loading this client’s files…</Empty>
          </CardBody>
        ) : rows.length === 0 ? (
          <CardBody>
            <Empty>Nothing has been stored for this client yet.</Empty>
          </CardBody>
        ) : (
          <>
            <CardBody tight>
              {shown.map((file) => (
                <FileRow
                  key={file.id}
                  icon={extensionOf(file.name)}
                  title={file.title}
                  sub={subOf(file, format)}
                  meta={
                    <>
                      <Num>{format.bytes(file.size)}</Num>
                      <StateOf file={file} />
                      <FileDialog file={file} onDone={refresh} />
                    </>
                  }
                />
              ))}
            </CardBody>

            <CardBody>
              <Stack gap="tight">
                {folded.length === 0 ? null : (
                  <Fold
                    summary={`The other ${format.qty(folded.length, {
                      one: 'file',
                      other: 'files',
                    })}`}
                  >
                    <ScrollX>
                      <Table aria-label="Everything else in this client’s library">
                        <TBody>
                          {folded.map((file) => (
                            <Tr key={file.id}>
                              <Td>{file.title}</Td>
                              <Td muted>
                                <Small>{subOf(file, format)}</Small>
                              </Td>
                              <Td right num>
                                {format.bytes(file.size)}
                              </Td>
                              <Td right>
                                <FileDialog file={file} onDone={refresh} />
                              </Td>
                            </Tr>
                          ))}
                        </TBody>
                      </Table>
                    </ScrollX>
                  </Fold>
                )}

                {/*
                  `files_list` pages at a hundred and this client has more, so the
                  page says which hundred rather than quietly showing the newest and
                  letting somebody conclude the older ones are gone.
                */}
                {list.data?.next_cursor == null ? null : (
                  <Small>
                    <Muted>
                      The {format.number(rows.length)} most recent are listed, of{' '}
                      {format.qty(files.files, { one: 'file', other: 'files' })}.
                    </Muted>
                  </Small>
                )}
              </Stack>
            </CardBody>
          </>
        )}
      </Card>
    </Section>
  )
}

/**
 * What is held, and why — the second half of the panel's opening sentence.
 *
 * Four states rather than one, because "held" and "gated" are two different facts
 * about a client's file and only one of them is about money. A page composes what it
 * was given; the split itself is `readmodels/files-summary.ts`'s.
 *
 * The last branch is the fallback for a payload recorded before `held_reasons`
 * existed: the count is still true, so it is still printed, and nothing claims a
 * reason nobody sent.
 */
function heldLine(files: ClientFilesSummaryView, format: Formatter): string {
  if (files.held === 0) return 'Nothing is held today.'

  const reasons = files.held_reasons
  if (reasons === null) {
    return `${format.qty(files.held, { one: 'item is', other: 'items are' })} still locked.`
  }

  /*
   * "waiting on that invoice", not "locked to one": the sentence this one completes
   * ends "…gated on **its final invoice**", and the ellipsis only reads while both
   * halves are in view. Rendered alone — which is every payload where nothing is held
   * for another reason — "4 items are locked to one." is an unfinished sentence. The
   * recorded world says exactly that, and said it the moment `held_reasons` stopped
   * being null.
   */
  const locked = `${format.qty(reasons.gated, {
    one: 'item is',
    other: 'items are',
  })} waiting on that invoice`
  const other = `${format.qty(reasons.ungated, {
    one: 'item is',
    other: 'items are',
  })} held back for another reason, behind no payment`

  if (reasons.ungated === 0) return `${locked}.`
  if (reasons.gated === 0) return `Nothing is waiting on an invoice, and ${other}.`
  return `${locked}, and ${other}.`
}

/* --------------------------------------------------------------------- a row */

/**
 * The one word a row carries, or none.
 *
 * `Pill state="need"` is a decision waiting on somebody (DESIGN.md P4), which is
 * exactly what these three are: a held file waits on a release, a requested review
 * waits on the client, and `changes_requested` waits on the studio to re-cut it.
 * Everything else gets nothing — CONTRACT.md §4.3, "good is the absence of ochre",
 * and a "Released" chip beside prose that says released is a chip §4.3 refuses.
 */
function StateOf({ file }: { readonly file: FileView }) {
  if (file.archived_at !== null) return <Pill>Archived</Pill>
  if (!file.released) return <Pill state="need">Held</Pill>
  if (file.review === 'requested') return <Pill state="need">Out for review</Pill>
  if (file.review === 'changes_requested') return <Pill state="need">Changes asked for</Pill>
  return null
}

/**
 * "deliverable · Aug 2026 · added 24 Aug 2026 · approved 22 Aug 2026" — the
 * `.xs muted` line under the title.
 *
 * ## Why an approval is a clause here and not a chip (R3-16)
 *
 * `StateOf` above badges the three states that are waiting on somebody, and a file
 * the client **approved** got nothing at all — so V-2608, signed off minutes before
 * V-2609 asked for changes, looked exactly like a file nobody had ever been asked
 * about. Those are very different facts and the answer was one click deep in
 * *Manage*.
 *
 * A fourth `Pill` would say it loudest, and it is the wrong device. DESIGN.md P4:
 * ochre means "this needs your decision", "good is not a colour — it is the absence
 * of ochre", and "any state that a sentence already communicates gets no chip at
 * all". An approval needs nobody, so a chip beside three that do would dilute the
 * three; a fourth colour is the vocabulary P4 shrank. What was missing was not a
 * signal, it was a **sentence** — and the sub-line is where this row already states
 * its facts, so the clause goes there and brings the date, which no chip could carry.
 * The client's own page words the same fact the same way
 * (`pages/client/files/MonthFiles.tsx` `reviewLineOf`: "you approved this on 22 Aug").
 *
 * `changes_requested` gets no clause: its pill is ochre because it is the studio's
 * move, and *Manage* is one click away with the words the client wrote. Repeating
 * "changes asked for" under a chip that says it is P7's footer that restates the
 * header.
 */
function subOf(file: FileView, format: Formatter): string {
  const parts = [file.kind]
  if (file.period !== null) parts.push(format.month(file.period))
  parts.push(`added ${format.date(file.created_at, 'short')}`)
  if (file.review === 'approved') {
    parts.push(
      file.reviewed_at === null
        ? 'approved by the client'
        : `approved ${format.date(file.reviewed_at, 'short')}`,
    )
  }
  return parts.join(' · ')
}

/**
 * Anything the studio or the client has to act on, then everything else newest first.
 *
 * The same rule and the same reason as `pages/client/files/MonthFiles.tsx`: a `slice`
 * down the middle of an unsorted list hides the held handover behind a disclosure,
 * and the held handover is the whole reason somebody opened this tab.
 *
 * Exported for its own test — the recorded world has one of each and the ordering is
 * the only thing on this page that is not a straight render of what it was given.
 */
export function byAttentionFirst(files: readonly FileView[]): readonly FileView[] {
  const needsSomebody = (file: FileView) =>
    file.archived_at === null &&
    (!file.released || file.review === 'requested' || file.review === 'changes_requested')
  return [...files.filter(needsSomebody), ...files.filter((file) => !needsSomebody(file))]
}
